import { camelToSnakeCase } from '../utils/utils';
import { useEventCallback } from './useEventCallback';
import useUserContext from '../providers/hooks/useUserContext';
import Avo, {
  AffiliationValueType,
  ConfiguratorSectionValueType,
  ConfiguratorTypeValueType,
  CmUserFlowValueType,
  CurrencyValueType,
} from '../Avo';
import {
  BookingToolEntryPoint,
  ConfiguratorOrigin,
  ConfiguratorOriginsType,
} from '../utils/constants';
import useNavigationContext from '../providers/hooks/useNavigationContext';
import useFabricContext from '../providers/hooks/useFabricContext';
import { ConfigurationTypes } from '../utils/productDefinition';

export const useAvo = () => {
  const { product, origin, sizeId, locationCode } = useUserContext();
  const { section } = useNavigationContext();
  const { selectedFabric } = useFabricContext();

  // The market currency is the fabric price list's. It is unknown until a fabric loads.
  const getCurrency = (): CurrencyValueType | null => {
    const code = selectedFabric?.prices?.code?.toLowerCase();
    return Object.values(Avo.Currency).find(value => value === code) ?? null;
  };

  // Falls back to the EUR store when the currency has no webstore, as the storefront does.
  const getAffiliation = (): AffiliationValueType =>
    Object.values(Avo.Affiliation).find(value => value === `webstore-${getCurrency()}`) ??
    Avo.Affiliation.WEBSTORE_EUR;

  const getUserFlow = (origin: ConfiguratorOriginsType): CmUserFlowValueType => {
    switch (origin) {
      case ConfiguratorOrigin.Normal:
        return Avo.CmUserFlow.NORMAL;
      case ConfiguratorOrigin.Reorder:
        return Avo.CmUserFlow.REORDER;
      case ConfiguratorOrigin.SaveForLater:
        return Avo.CmUserFlow.SAVE_FOR_LATER;
      case ConfiguratorOrigin.FinishInStore:
        return Avo.CmUserFlow.FINISH_IN_STORE;
      case ConfiguratorOrigin.Clone:
        return Avo.CmUserFlow.CLONE;
      case ConfiguratorOrigin.CustomizeFlow:
        if (sizeId) {
          return Avo.CmUserFlow.CUSTOMIZE_FLOW_SIZE_PICKER;
        }
        return Avo.CmUserFlow.CUSTOMIZE_FLOW_PDP;
      default:
        return Avo.CmUserFlow.NORMAL;
    }
  };

  const getAvoCategories = (configurationType: ConfigurationTypes | string) => {
    switch (configurationType) {
      case ConfigurationTypes.Jacket:
      case ConfigurationTypes.TuxedoJacket:
        return 'jackets';
      case ConfigurationTypes.Trousers:
      case ConfigurationTypes.ExtraTrouser:
      case ConfigurationTypes.TuxedoTrouser:
      case ConfigurationTypes.ExtraTuxedoTrouser:
        return 'trousers';
      case ConfigurationTypes.Waistcoat:
      case ConfigurationTypes.TuxedoWaistcoat:
        return 'waistcoats';
      case ConfigurationTypes.Coat:
        return 'coats';
      case ConfigurationTypes.Shirt:
      case ConfigurationTypes.TuxedoShirt:
        return 'shirts';
      default:
        return 'jackets';
    }
  };

  const standardEventProps = () => ({
    eventLocation: `cm_${camelToSnakeCase(product!)}_${camelToSnakeCase(section!)}`,
    eventCategory: Avo.EventCategory.CM_INTERACTIONS,
    eventLabel: '',
    cmUserFlow: getUserFlow(origin),
    currency: getCurrency(),
  });

  const standardCmProps = (detailedSection: string, detailedType?: string) => {
    const getConfiguratorType = () => {
      if (detailedType) {
        return detailedType;
      }

      if ((product === 'Suit' || product === 'Tuxedo') && section !== 'fabric' && section !== 'summary') {
        return `${camelToSnakeCase(product!)}_${camelToSnakeCase(section!)}`;
      }

      return camelToSnakeCase(product!);
    };

    return {
      configuratorType: getConfiguratorType() as ConfiguratorTypeValueType,
      configuratorSection: camelToSnakeCase(detailedSection) as ConfiguratorSectionValueType,
    };
  };

  // Page-level values (country, language, location_id, user_id, logged_in, page_type) are not
  // event params: the shared client takes them from the page and from the context Custom Made
  // declares as page owner (see initializeAnalytics).
  const getAvoStandardEventParams = useEventCallback((detailedSection: string, detailedType?: string) => ({
    ...standardEventProps(),
    ...standardCmProps(detailedSection, detailedType),
  }));

  /** Standard params for the two links that open the booking tool (DA-1554): promotion_id and
   * creative per entry point, name/position fixed. See Avo.ts jsdoc on promotionViewed/promotionClicked. */
  const getBookingToolPromotionParams = (entryPoint: BookingToolEntryPoint) => ({
    name: 'booking_tool',
    promotionId: `booking_tool_cm_${entryPoint}`,
    creative:
      entryPoint === BookingToolEntryPoint.FinishInStore
        ? Avo.Creative.CM_SUMMARY_PANEL
        : Avo.Creative.CM_SIZE_PASSPORT,
    position: 1,
    // The store's name isn't known here, only its location code (set at init when used in-store).
    storeName: undefined,
    // Omitted online, where there is no store.
    locationCode: locationCode || undefined,
    // Not applicable to booking-tool links.
    shopTheLookId: '',
  });

  return {
    getAvoStandardEventParams,
    getAvoCategories,
    getAffiliation,
    getBookingToolPromotionParams,
  };
};
